import { groupSigs, pairKey } from "../types/api";
import type { ManualMatchPair, ManualUnresolved } from "../types/api";

// The ONE list of manual matches on the Manual Match tab: the per-insurer remembered store,
// plus any match made this session that the store does not hold. applyPairs saves before it
// finalizes, so that set is only a match whose save FAILED — and it still applies, because
// the session list rides every finalize. Leaving it out would move a figure while listing
// nothing (CLAUDE.md §6), which is why this replaced a separate "applied this session" list.
export interface SavedMatch {
  pair: ManualMatchPair;
  key: string;
  applied: boolean;            // the engine applied it this run
  saved: boolean;              // held by the remembered store
  thisSession: boolean;        // made in this session
  reason?: ManualUnresolved;   // the engine's refusal, when not applied
  rolled?: string;             // applied to prior-period items: "Group 3" or "settled"
  status: string;              // Status column, verbatim
  origin: string;              // "Made in" column, verbatim
}

export interface SavedMatches {
  items: SavedMatch[];
  // Refusals naming no leg of any listed match (a malformed stored entry, or a store that
  // could not be read). The separate "not applied" list is folded into this one, so these
  // are rendered on their own rather than dropped.
  orphans: ManualUnresolved[];
}

export function savedMatchStatus(applied: boolean, reason?: ManualUnresolved): string {
  if (applied) return "Applied this run";
  if (reason?.reason.includes("ambiguous")) return "Not applied: ambiguous";
  // Neither a missing nor an ambiguous row (a malformed same-book entry, a one-row group):
  // the engine's own words, not a label that sends finance looking for a missing row.
  if (reason && !reason.reason.startsWith("no matching")) return `Not applied: ${reason.reason}`;
  return "Not applied: no matching row";
}

// A match whose rows rolled forward applied to those prior-period items instead.
export function rolledMatchStatus(settled: boolean): string {
  return `Applied to prior-period items (${settled ? "settled" : "Group 3"})`;
}

// The list view's badge for a match that did not apply, and whether it re-applies by itself.
// Only a missing or ambiguous row can resolve on a later upload; a row the engine judged
// edited, re-valued or unconfirmed does not, so the tip must not promise it.
export function savedMatchBadge(reason?: ManualUnresolved): { label: string; reapplies: boolean } {
  const why = reason?.reason ?? "";
  if (why.includes("ambiguous")) return { label: "Ambiguous", reapplies: true };
  if (!reason || why.startsWith("no matching")) return { label: "No matching row", reapplies: true };
  if (why.includes("edited in the ledger")) return { label: "Row changed", reapplies: false };
  if (why.startsWith("its amount has changed")) return { label: "Amount changed", reapplies: false };
  if (why.startsWith("its row can't be confirmed")) return { label: "Row not confirmed", reapplies: false };
  // Before "can't be hand-matched", which this refusal also contains (carried_rolled.NOT_OPEN_AT_START).
  if (why.includes("no prior-period list you uploaded")) return { label: "Not on prior-period list", reapplies: false };
  if (why.includes("can't be hand-matched")) return { label: "Outside Group 1/2", reapplies: false };
  return { label: "Not applied", reapplies: false };
}

export function buildSavedMatches(
  remembered: ManualMatchPair[],
  sessionPairs: ManualMatchPair[],
  appliedKeys: Set<string>,
  unresolved: ManualUnresolved[],
  rolled: Map<string, boolean> = new Map(),   // key -> settled (result.manual_rolled)
): SavedMatches {
  const bySig = new Map<string, ManualUnresolved>();
  for (const u of unresolved) if (u?.signature) bySig.set(u.signature, u);
  const storeKeys = new Set(remembered.map(pairKey));
  const sessionKeys = new Set(sessionPairs.map(pairKey));
  // Store order first (what finance already knows), unsaved session matches after.
  const pairs = [...remembered, ...sessionPairs.filter((p) => !storeKeys.has(pairKey(p)))];

  const claimed = new Set<string>();
  const items = pairs.map((pair): SavedMatch => {
    const key = pairKey(pair);
    const { cri, ins } = groupSigs(pair);
    const legs = [...cri, ...ins];
    for (const s of legs) claimed.add(s);
    const settled = rolled.get(key);
    const applied = appliedKeys.has(key) || settled !== undefined;
    // The first refusal on any leg — the engine reports only the leg that failed.
    const reason = applied ? undefined : legs.map((s) => bySig.get(s)).find(Boolean);
    const saved = storeKeys.has(key);
    return {
      pair, key, applied, saved, reason,
      rolled: settled === undefined ? undefined : settled ? "settled" : "Group 3",
      thisSession: sessionKeys.has(key),
      status: settled === undefined ? savedMatchStatus(applied, reason) : rolledMatchStatus(settled),
      origin: !saved ? "This session (not saved)"
        : sessionKeys.has(key) ? "This session" : "Earlier session",
    };
  });
  const orphans = unresolved.filter((u) => !u?.signature || !claimed.has(u.signature));
  return { items, orphans };
}
